import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import Header from '../../components/header/header.component';
import ModuleSummary from '../../components/module-summary/module-summary.component';
import ResponseCodes from '../../constants/response.codes';
import { subscriptionSdk } from '../../services/subscription.service';
import { useAuthStore, useThemeStore } from '../../store';
import View from '../../components/view/view';
import { usePermissions } from '../../hooks/usePermissions';
import ApiPermissions from '../../constants/api-permissions.types';
import { Service } from '../../types/service.type';

import { totalsMapper } from './utils';
import ServicesGrid from './components/service-card-grid';
import UpdateServiceModal from './components/update-service-modal';

const ServicesView = () => {
    const { theme } = useThemeStore();
    const { permissions } = useAuthStore();
    const hasPermission = usePermissions();

    const [totalSummary, setTotalSummary] = useState([
        { label: 'Total servicios', value: '-' },
        { label: 'Servicios con planes', value: '-' },
        { label: 'Servicios sin planes', value: '-' },
    ]);
    const [selectedService, setSelectedService] = useState<Service | null>(
        null
    );
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

    const handleServiceUpdate = (service: Service) => {
        setSelectedService(service);
        setIsUpdateModalOpen(true);
    };

    const handleCloseUpdateModal = () => {
        setIsUpdateModalOpen(false);
        setSelectedService(null);
    };

    const { data: servicesData, isPending } = useQuery({
        queryKey: ['services'],
        queryFn: async () => {
            const response = await subscriptionSdk.getAllServices();
            if (response.code !== ResponseCodes.PROCESS_OK) {
                throw new Error('Error getting services');
            }
            return response.data;
        },
        enabled: hasPermission(ApiPermissions.READ_SERVICES),
    });

    useEffect(() => {
        if (servicesData) {
            const totalsData: Record<string, string> = {
                total: servicesData.length.toString(),
                withPlans: servicesData
                    .filter(
                        (service) => service.plans && service.plans.length > 0
                    )
                    .length.toString(),
                withoutPlans: servicesData
                    .filter(
                        (service) =>
                            !service.plans || service.plans.length === 0
                    )
                    .length.toString(),
            };

            const summary = Object.keys(totalsData).map((key) => ({
                label: totalsMapper[key],
                value: totalsData[key],
            }));

            setTotalSummary(summary);
        }
    }, [servicesData]);

    return (
        <View permissions={permissions} requiredPermission="read-services">
            <Header
                title={'Servicios'}
                subtitle={'Aquí puedes ver todos los servicios'}
            />
            <ModuleSummary mode={theme} summary={totalSummary} />
            {/* {hasPermission(ApiPermissions.CREATE_SERVICES) && (
                <CreateServiceModal />
            )} */}

            {hasPermission(ApiPermissions.UPDATE_SERVICES) &&
                selectedService && (
                    <UpdateServiceModal
                        service={selectedService}
                        isOpen={isUpdateModalOpen}
                        onClose={handleCloseUpdateModal}
                    />
                )}
            {isPending ? (
                'Cargando...'
            ) : (
                <ServicesGrid
                    services={servicesData || []}
                    onUpdateService={
                        hasPermission(ApiPermissions.UPDATE_SERVICES)
                            ? handleServiceUpdate
                            : () =>
                                  alert(
                                      'No tienes permisos para actualizar servicios'
                                  )
                    }
                />
            )}
        </View>
    );
};

export default ServicesView;
